import { Type } from '@angular/core';
import { WidgetTemplateTableComponent } from '../widgets/widget-template-table/widget-template-table.component';
import { WidgetTemplateTrendComponent } from '../widgets/widget-template-trend/widget-template-trend.component';
import { DashboardJson, Dashboard, WidgetJson, Widget } from '../models/dashboard';
import { ActivityWidgetComponent } from '../widgets/activity-widget/activity-widget.component';
import { WidgetComponentType } from '../enum/widget-component-type.enum';
import { ChartWidgetComponent } from '../widgets/chart-widget/chart-widget.component';
import { MultiLineChartWidgetComponent } from '../widgets/multi-line-chart-widget/multi-line-chart-widget.component';

/**
 * Mappt jeden WidgetComponentType auf die entsprechende Angular-Komponente.
 * Das Record-Interface erlaubt es, über den numerischen Schlüssel (WidgetComponentType)
 * direkt die zugehörige Komponente abzurufen.
 */
export const componentMap: Record<WidgetComponentType, Type<unknown>> = {
  // Jede Zahl (als String) entspricht einem Wert aus WidgetComponentType
  "0": ActivityWidgetComponent,
  "1": ChartWidgetComponent,
  "2": WidgetTemplateTableComponent,
  "3": WidgetTemplateTrendComponent,
  "4": MultiLineChartWidgetComponent,
};

/**
 * Wandelt ein Array von DashboardJson-Objekten in echte Dashboard-Instanzen um.
 * Dabei werden Datumsfelder in Date-Objekte konvertiert und der content-Wert
 * in den Widgets anhand der componentMap auf die tatsächliche Komponente gesetzt.
 * 
 * @param json - Array von rohen DashboardJson-Objekten aus der API
 * @returns Array von Dashboard-Objekten mit korrekten Typen und Komponenten-Referenzen
 */
export function resolveDashboards(json: DashboardJson[]): Dashboard[] {
  return json.map(d => ({
    ...d,
    // Wandelt den ISO-String in ein Date-Objekt um
    createdAt: new Date(d.createdAt),
    updatedAt: new Date(d.updatedAt),
    // Jedes Widget-Json bekommt eine content-Eigenschaft, die auf die Komponente zeigt
    widgets: d.widgets.map(w => ({
      ...w,
      content: componentMap[w.componentType]
    }))
  }));
}

/**
 * Wandelt ein einzelnes DashboardJson-Objekt in ein Dashboard-Objekt um.
 * Funktioniert analog zu resolveDashboards, nur für einen einzelnen Eintrag.
 * 
 * @param json - Ein rohes DashboardJson-Objekt aus der API
 * @returns Dashboard-Objekt mit korrekten Datums- und Komponenten-Referenzen
 */
export function resolveDashboard(json: DashboardJson): Dashboard {
  return {
    ...json,
    createdAt: new Date(json.createdAt),
    updatedAt: new Date(json.updatedAt),
    widgets: json.widgets.map(w => ({
      ...w,
      content: componentMap[w.componentType]
    }))
  };
}

/**
 * Wandelt ein einzelnes WidgetJson-Objekt in ein Widget-Objekt um.
 * Setzt dabei das content-Feld auf die passende Angular-Komponente.
 * 
 * @param json - Ein rohes WidgetJson-Objekt aus der API
 * @returns Widget-Objekt mit componentType in content konvertiert
 */
export function resolveWidget(json: WidgetJson): Widget {
  return {
    ...json,
    content: componentMap[json.componentType]
  };
}

/**
 * Serialisiert ein Dashboard-Objekt zurück in das JSON-Format (DashboardJson),
 * das an die API gesendet werden kann. Dabei werden Date-Objekte in ISO-Strings
 * zurückverwandelt und für jedes Widget getComponentTypeFromComponent aufgerufen,
 * um den componentType-Wert zu ermitteln.
 * 
 * @param dashboard - Ein Dashboard-Objekt zur Serialisierung
 * @returns DashboardJson-Objekt bereit für die API
 */
export function serializeDashboard(dashboard: Dashboard): DashboardJson {
  return {
    ...dashboard,
    // Date-Objekte zurück in ISO-Strings umwandeln
    createdAt: dashboard.createdAt.toISOString(),
    updatedAt: dashboard.updatedAt.toISOString(),
    // Jedes Widget in ein JSON-Format konvertieren
    widgets: dashboard.widgets.map(serializeWidget)
  };
}

/**
 * Serialisiert ein Array von Dashboard-Objekten in DashboardJson-Objekte.
 * 
 * @param dashboards - Array von Dashboard-Objekten
 * @returns Array von DashboardJson-Objekten
 */
export function serializeDashboards(dashboards: Dashboard[]): DashboardJson[] {
  return dashboards.map(serializeDashboard);
}

/**
 * Serialisiert ein einzelnes Widget-Objekt in WidgetJson-Format.
 * Dabei wird getComponentTypeFromComponent genutzt, um den numerischen
 * componentType aus der Angular-Komponente (content) zu ermitteln.
 * 
 * @param widget - Ein Widget-Objekt zur Serialisierung
 * @returns WidgetJson-Objekt bereit für die API
 */
export function serializeWidget(widget: Widget): WidgetJson {
  return {
    ...widget,
    componentType: getComponentTypeFromComponent(widget.content)
  };
}

/**
 * Gibt den entsprechenden `WidgetComponentType` für eine gegebene Komponente zurück.
 * Diese Funktion verwendet eine `switch`-Anweisung, um die Komponente mit ihrem zugehörigen 
 * `WidgetComponentType` zu verknüpfen. Falls die Komponente nicht in der Zuordnung enthalten ist, 
 * wird eine Fehlermeldung ausgelöst.
 * 
 * @param component - Die Komponente, deren Typ ermittelt werden soll. Muss vom Typ `Type<unknown>` sein.
 * @returns Der zugehörige `WidgetComponentType` der Komponente.
 * 
 * @throws {Error} Wenn die Komponente nicht in der Zuordnung enthalten ist.
 */
function getComponentTypeFromComponent(component: Type<unknown>): WidgetComponentType {
  switch(component) {
    case ActivityWidgetComponent:
      return WidgetComponentType.ActivityComponent;
    case WidgetTemplateTrendComponent:
      return WidgetComponentType.TrendComponent;
    case WidgetTemplateTableComponent:
      return WidgetComponentType.TableComponent;
    case ChartWidgetComponent:
      return WidgetComponentType.ChartComponent;
    case MultiLineChartWidgetComponent:
        return WidgetComponentType.MultiLineChartWidgetComponent;
    default:
      // Falls die Komponente nicht gefunden wird, werfen wir einen Fehler
      throw new Error(`Component not found in componentMap: ${component}`);
  }
}
